import React, {useState, createContext, useContext} from "react";
import { OrgaAndRoles } from "../models/OrgaAndRoles";
import {StorageKey} from "../utils/storage/Keys";

function useSelectedOrganisation() {
  const [selectedOrganisation, setSelectedOrganisation] = useState<OrgaAndRoles | null>(() => {
    const stored = localStorage.getItem(StorageKey.SELECTED_ORGANISATION);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        // ignore parse errors
      }
    }
    return null;
  });

  const updateOrganisation = (org: OrgaAndRoles) => {
    setSelectedOrganisation(org);
    localStorage.setItem(StorageKey.SELECTED_ORGANISATION, JSON.stringify(org));
  };

  return { selectedOrganisation, setSelectedOrganisation: updateOrganisation };
}

export interface OrganisationContextValue {
  selectedOrganisation: OrgaAndRoles | null;
  setSelectedOrganisation: (org: OrgaAndRoles) => void;
}

const OrganisationContext = createContext<OrganisationContextValue | undefined>(undefined);

export const OrganisationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { selectedOrganisation, setSelectedOrganisation } = useSelectedOrganisation();
  return (
      <OrganisationContext.Provider value={{ selectedOrganisation, setSelectedOrganisation }}>
        {children}
      </OrganisationContext.Provider>
  );
};

export function useSelectedOrganisationContext() {
  const ctx = useContext(OrganisationContext);
  if (!ctx) throw new Error("useOrganisationContext must be used within OrganisationProvider");
  return ctx;
}

export function useSelectedOrganisationId() {
  return useSelectedOrganisationContext().selectedOrganisation?.id ?? '';
}

export function requireValue(value: string | undefined, message: string) {
  if (!value) {
    throw new Error(message);
  }
  return value;
}

export function useRequiredOrganisationId() {
  return requireValue(
    useSelectedOrganisationContext().selectedOrganisation?.id,
    'A selected organisation is required for this page.',
  );
}
